/**
 * GM Add to Cart — card / reco (spec 0054) + properties (spec 0055) + multi-item combo (spec 0067).
 * POST cart/add.js + CartAddEvent (abre drawer se auto-open).
 * stopPropagation evita navegar no <a> do card.
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const ADDING_CLASS = 'is-adding';

/**
 * @returns {string[]}
 */
function collectCartSectionIds() {
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });
  return sectionIds;
}

/**
 * @param {HTMLButtonElement | null | undefined} btn
 * @param {boolean} busy
 */
function setButtonBusy(btn, busy) {
  if (!btn) return;
  if (busy) {
    btn.classList.add(ADDING_CLASS);
    btn.setAttribute('aria-busy', 'true');
  } else {
    btn.classList.remove(ADDING_CLASS);
    btn.removeAttribute('aria-busy');
  }
}

/**
 * @param {{
 *   variantId: string,
 *   quantity?: number,
 *   productId?: string,
 *   properties?: Record<string, string>,
 *   button?: HTMLButtonElement | null,
 * }} opts
 */
export async function addVariantToCart(opts) {
  const variantId = opts.variantId;
  if (!variantId) return;

  const quantity = Number(opts.quantity) || 1;
  const productId = opts.productId || '';
  const btn = opts.button || null;

  if (btn) {
    if (btn.disabled || btn.classList.contains(ADDING_CLASS)) return;
    setButtonBusy(btn, true);
  }

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', String(quantity));

  if (opts.properties) {
    for (const [key, value] of Object.entries(opts.properties)) {
      if (value == null || value === '') continue;
      formData.append(`properties[${key}]`, String(value));
    }
  }

  const sectionIds = collectCartSectionIds();
  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
  }

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });
    const data = await response.json();

    if (data.status) {
      document.dispatchEvent(
        new CartErrorEvent('gm-add-to-cart', data.message, data.description, data.errors)
      );
      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          didError: true,
          source: 'gm-add-to-cart',
          itemCount: quantity,
          productId,
        })
      );
      throw new Error(data.description || data.message || 'cart_add_error');
    }

    let cart = null;
    try {
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
      cart = await cartRes.json();
    } catch (_) {
      /* morph ainda funciona com sections na resposta do add */
    }

    document.dispatchEvent(
      new CartAddEvent(cart ?? undefined, variantId, {
        source: 'gm-add-to-cart',
        itemCount: quantity,
        productId,
        sections: data.sections,
      })
    );
  } finally {
    setButtonBusy(btn, false);
  }
}

/**
 * Multi-item ATC (PDP Compre Junto / spec 0067).
 * @param {{
 *   items: Array<{ variantId?: string, id?: string | number, quantity?: number }>,
 *   button?: HTMLButtonElement | null,
 * }} opts
 */
export async function addItemsToCart(opts) {
  const rawItems = Array.isArray(opts.items) ? opts.items : [];
  const items = rawItems
    .map((item) => ({
      id: Number(item.variantId || item.id),
      quantity: Number(item.quantity) || 1,
    }))
    .filter((item) => item.id);

  if (!items.length) return;

  const btn = opts.button || null;
  if (btn) {
    if (btn.disabled || btn.classList.contains(ADDING_CLASS)) return;
    setButtonBusy(btn, true);
  }

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const firstId = String(items[0].id);
  const sectionIds = collectCartSectionIds();
  /** @type {{ items: Array<{ id: number, quantity: number }>, sections?: string }} */
  const payload = { items };
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    if (data.status) {
      document.dispatchEvent(
        new CartErrorEvent('gm-add-to-cart', data.message, data.description, data.errors)
      );
      document.dispatchEvent(
        new CartAddEvent({}, firstId, {
          didError: true,
          source: 'gm-add-to-cart-combo',
          itemCount,
        })
      );
      throw new Error(data.description || data.message || 'cart_add_error');
    }

    let cart = null;
    try {
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
      cart = await cartRes.json();
    } catch (_) {
      /* morph com sections */
    }

    document.dispatchEvent(
      new CartAddEvent(cart ?? undefined, firstId, {
        source: 'gm-add-to-cart-combo',
        itemCount,
        sections: data.sections,
      })
    );
  } finally {
    setButtonBusy(btn, false);
  }
}

/**
 * @param {HTMLButtonElement} btn
 */
async function addFromButton(btn) {
  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  await addVariantToCart({
    variantId,
    quantity: Number(btn.dataset.quantity) || 1,
    productId: btn.dataset.productId || '',
    button: btn,
  });
}

/**
 * @param {HTMLButtonElement} btn
 */
async function addItemsFromButton(btn) {
  const raw = btn.dataset.items;
  if (!raw) return;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return;
  }

  if (!Array.isArray(parsed) || !parsed.length) return;

  const items = parsed.map((row) => ({
    variantId: String(row.id ?? row.variantId ?? ''),
    quantity: Number(row.quantity) || 1,
  }));

  await addItemsToCart({ items, button: btn });
}

document.addEventListener(
  'click',
  (e) => {
    const comboBtn = e.target instanceof Element ? e.target.closest('[data-gm-add-items]') : null;
    if (comboBtn instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
      addItemsFromButton(comboBtn);
      return;
    }

    const btn = e.target instanceof Element ? e.target.closest('[data-gm-add-to-cart]') : null;
    if (!btn || !(btn instanceof HTMLButtonElement)) return;
    if (btn.dataset.gmPersonalizable === 'true') return;
    e.preventDefault();
    e.stopPropagation();
    addFromButton(btn);
  },
  true
);

/**
 * Apple Pay / accelerated: só em dispositivo Apple elegível (spec 0062).
 * Shopify já omite wallets indisponíveis; o tema esconde o bloco no resto.
 * Mobile: payment_button fica oculto no form; CTA visual vive no body
 * (`[data-gm-pdp-apple-pay]`) e dispara o botão real.
 *
 * TEMP 2026-08-06: desligado. Religar: trocar para true (+ Liquid em
 * gm-pdp-buy-foot / gm-add-to-cart).
 */
const GM_APPLE_PAY_ENABLED = false;

function isAppleAcceleratedHost() {
  if (!GM_APPLE_PAY_ENABLED) return false;
  if (typeof window.ApplePaySession === 'function') {
    try {
      if (window.ApplePaySession.canMakePayments?.()) return true;
    } catch {
      /* ignore */
    }
  }
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && 'ontouchend' in document) return true;
  return false;
}

function hideShopifyMorePaymentOptions(root = document) {
  root.querySelectorAll('.shopify-payment-button__more-options, more-payment-options-link').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = true;
    el.setAttribute('aria-hidden', 'true');
    el.style.setProperty('display', 'none', 'important');
  });
}

function revealAcceleratedCheckout() {
  if (!isAppleAcceleratedHost()) return;
  let ready = false;
  document.querySelectorAll('[data-gm-atc-accelerated]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const hasButton =
      el.querySelector('shopify-accelerated-checkout') ||
      el.querySelector('[data-shopify-buttoncontainer]') ||
      el.querySelector('.shopify-payment-button') ||
      el.childElementCount > 0;
    if (!hasButton) return;
    el.hidden = false;
    el.classList.add('is-apple-ready');
    hideShopifyMorePaymentOptions(el);
    ready = true;
  });
  if (ready) {
    document.documentElement.setAttribute('data-gm-apple-ready', 'true');
  }
}

function triggerAcceleratedCheckout() {
  const root = document.querySelector('[data-gm-atc-accelerated].is-apple-ready, [data-gm-atc-accelerated]:not([hidden])');
  if (!(root instanceof HTMLElement)) return false;

  const candidates = [
    root.querySelector('.shopify-payment-button__button'),
    root.querySelector('[data-shopify-buttoncontainer] button'),
    root.querySelector('shopify-accelerated-checkout'),
    root.querySelector('button'),
  ].filter(Boolean);

  for (const node of candidates) {
    if (!(node instanceof HTMLElement)) continue;
    node.click();
    return true;
  }
  return false;
}

document.addEventListener(
  'click',
  (e) => {
    const apple = e.target instanceof Element ? e.target.closest('[data-gm-pdp-apple-pay]') : null;
    if (!apple || !(apple instanceof HTMLElement)) return;
    if (apple.closest('.gm-pdp-sticky-bar')) return;

    const dual = apple.closest('[data-gm-dual-toggle]');
    if (
      dual &&
      window.matchMedia('(max-width: 749px)').matches &&
      (dual.getAttribute('data-gm-dual-expanded') || 'primary') !== 'primary'
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (!triggerAcceleratedCheckout()) {
      revealAcceleratedCheckout();
      requestAnimationFrame(() => triggerAcceleratedCheckout());
    }
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', revealAcceleratedCheckout);
} else {
  revealAcceleratedCheckout();
}
window.addEventListener('load', revealAcceleratedCheckout);
